import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  Link2, Unlink, Activity, Settings, Database, Cpu, Globe, Terminal, 
  CheckCircle, AlertCircle, XCircle, Plus, Search, Trash2, RefreshCw, 
  Eye, EyeOff, Radio, Lock, Clock
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface IntegrationConnector {
  id: string;
  name: string;
  category: string;
  description: string;
  logoText: string;
  status: 'Conectado' | 'Configurado' | 'Disponível' | string;
  apiEndpoint?: string;
  clientId?: string;
  clientSecret?: string;
  lastSync?: string;
}

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: string;
  secret: string;
  createdAt: string;
}

interface IntegrationLog {
  id: string;
  timestamp: string;
  integration: string;
  direction: string;
  event: string;
  status: string;
  latency: number;
  responseCode: number;
}

export default function IntegrationsAdminView() {
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState<'connectors' | 'webhooks' | 'logs'>('connectors');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConnector, setSelectedConnector] = useState<IntegrationConnector | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

  // Form states
  const [configForm, setConfigForm] = useState({
    apiEndpoint: '',
    clientId: '',
    clientSecret: '',
  });

  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: ['*'],
  });

  // Queries
  const { data: connectors = [], isLoading: loadingConnectors, refetch: refetchConnectors } = useQuery<IntegrationConnector[]>({
    queryKey: ['admin-integrations'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/integrations');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  const { data: webhooks = [], isLoading: loadingWebhooks, refetch: refetchWebhooks } = useQuery<WebhookEndpoint[]>({
    queryKey: ['admin-webhooks'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/integrations/webhooks');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeSubTab === 'webhooks',
  });

  const { data: logs = [], isLoading: loadingLogs } = useQuery<IntegrationLog[]>({
    queryKey: ['admin-integration-logs'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/integrations/logs');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeSubTab === 'logs',
  });

  // Mutations
  const toggleMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/admin/integrations/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      showSuccess('Status da integração atualizado!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao alternar integração.');
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.put(`/api/admin/integrations/${id}/config`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      setIsConfigModalOpen(false);
      showSuccess('Configuração da integração salva!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao salvar configuração.');
    },
  });

  const createWebhookMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/admin/integrations/webhooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-webhooks'] });
      setIsWebhookModalOpen(false);
      showSuccess('Webhook cadastrado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao criar webhook.');
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/admin/integrations/webhooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-webhooks'] });
      showSuccess('Webhook removido com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao remover webhook.');
    },
  });

  const handleOpenConfig = (conn: IntegrationConnector) => {
    setSelectedConnector(conn);
    setConfigForm({
      apiEndpoint: conn.apiEndpoint || '',
      clientId: conn.clientId || '',
      clientSecret: '',
    });
    setIsConfigModalOpen(true);
  };

  const handleSaveConfig = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConnector) return;
    updateConfigMutation.mutate({ id: selectedConnector.id, data: configForm });
  };

  const handleCreateWebhook = (e: React.FormEvent) => {
    e.preventDefault();
    if (!webhookForm.name || !webhookForm.url) return;
    createWebhookMutation.mutate(webhookForm);
  };

  const filteredConnectors = connectors.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.category.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Radio size={14} /> Barramento & Integrações
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Conectores, APIs & Webhooks
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Gerenciamento centralizado de conectores de terceiros (ERP, CRM, Gateways) e webhooks de eventos.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('connectors')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'connectors'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Conectores Oficiais ({connectors.length})
        </button>
        <button
          onClick={() => setActiveSubTab('webhooks')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'webhooks'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Webhooks Externos
        </button>
        <button
          onClick={() => setActiveSubTab('logs')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'logs'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Logs de Tráfego
        </button>
      </div>

      {/* Tab: Connectors */}
      {activeSubTab === 'connectors' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar conector..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white"
              />
            </div>
            <button
              onClick={() => refetchConnectors()}
              className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-semibold transition-all"
            >
              <RefreshCw size={13} className={loadingConnectors ? 'animate-spin' : ''} /> Atualizar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConnectors.map((conn) => (
              <div
                key={conn.id}
                className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col justify-between hover:border-indigo-200 transition-all"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold text-xs">
                        {conn.logoText || conn.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-gray-900">{conn.name}</h4>
                        <span className="text-[10px] text-gray-400 font-medium">{conn.category}</span>
                      </div>
                    </div>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        conn.status === 'Conectado'
                          ? 'bg-emerald-50 text-emerald-700'
                          : conn.status === 'Configurado'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {conn.status}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed min-h-[3rem] line-clamp-2">
                    {conn.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <button
                    onClick={() => handleOpenConfig(conn)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <Settings size={13} /> Configurar
                  </button>
                  <button
                    onClick={() => toggleMutation.mutate(conn.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition-all ${
                      conn.status === 'Conectado'
                        ? 'text-rose-600 hover:bg-rose-50'
                        : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    {conn.status === 'Conectado' ? (
                      <>
                        <Unlink size={13} /> Desconectar
                      </>
                    ) : (
                      <>
                        <Link2 size={13} /> Conectar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Webhooks */}
      {activeSubTab === 'webhooks' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setIsWebhookModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs"
            >
              <Plus size={14} /> Novo Webhook
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
            {loadingWebhooks ? (
              <div className="p-12 text-center text-xs text-gray-500">Carregando webhooks...</div>
            ) : webhooks.length === 0 ? (
              <div className="p-12 text-center text-xs text-gray-400">Nenhum webhook externo cadastrado.</div>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Nome</th>
                    <th className="py-3 px-4">URL de Destino</th>
                    <th className="py-3 px-4">Eventos</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {webhooks.map((wh) => (
                    <tr key={wh.id} className="hover:bg-gray-50/50">
                      <td className="py-3.5 px-4 font-bold text-gray-900">{wh.name}</td>
                      <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500">{wh.url}</td>
                      <td className="py-3.5 px-4">
                        <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-[10px] font-semibold">
                          {wh.events?.join(', ') || '*'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {wh.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => deleteWebhookMutation.mutate(wh.id)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-gray-100"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Tab: Logs */}
      {activeSubTab === 'logs' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
          {loadingLogs ? (
            <div className="p-12 text-center text-xs text-gray-500">Carregando logs de integração...</div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
                <tr>
                  <th className="py-3 px-4">ID</th>
                  <th className="py-3 px-4">Integração</th>
                  <th className="py-3 px-4">Direção</th>
                  <th className="py-3 px-4">Evento</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Latência</th>
                  <th className="py-3 px-4">Data / Hora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50">
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500">{log.id}</td>
                    <td className="py-3.5 px-4 font-semibold text-gray-900">{log.integration}</td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-[10px] font-semibold">
                        {log.direction}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-700">{log.event}</td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        log.status === 'Sucesso' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                      }`}>
                        {log.status} ({log.responseCode})
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500">{log.latency}ms</td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Config Modal */}
      {isConfigModalOpen && selectedConnector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-1">
              Configurar {selectedConnector.name}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Informe as credenciais de autenticação da API.
            </p>

            <form onSubmit={handleSaveConfig} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Endpoint da API</label>
                <input
                  type="text"
                  value={configForm.apiEndpoint}
                  onChange={(e) => setConfigForm({ ...configForm, apiEndpoint: e.target.value })}
                  placeholder="https://api..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Client ID / Token de Acesso</label>
                <input
                  type="text"
                  value={configForm.clientId}
                  onChange={(e) => setConfigForm({ ...configForm, clientId: e.target.value })}
                  placeholder="ID ou chave pública"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Client Secret / Senha</label>
                <input
                  type="password"
                  value={configForm.clientSecret}
                  onChange={(e) => setConfigForm({ ...configForm, clientSecret: e.target.value })}
                  placeholder="Chave secreta"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsConfigModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateConfigMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl"
                >
                  {updateConfigMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Webhook Modal */}
      {isWebhookModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-1">Cadastrar Webhook</h3>
            <p className="text-xs text-gray-500 mb-4">Receba notificações de eventos em tempo real.</p>

            <form onSubmit={handleCreateWebhook} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  required
                  value={webhookForm.name}
                  onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                  placeholder="Ex: ERP Integrator"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">URL Endpoint</label>
                <input
                  type="url"
                  required
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  placeholder="https://sua-api.com/webhook"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsWebhookModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createWebhookMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl"
                >
                  {createWebhookMutation.isPending ? 'Salvando...' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
